import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, pickImage } from "@/lib/api";
import { Header } from "./IndustriesPanel";
import { useSite } from "@/context/SiteContext";

export default function FeaturesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("");
  const site = useSite();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/features", { params: filterCat ? { category_id: filterCat } : {} });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filterCat]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="features-panel" className="space-y-6">
      <Header
        title="Features"
        subtitle="Every camera use-case shown on the public site."
        right={
          <Link
            to="/admin/features/new"
            data-testid="feature-new"
            className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-brand text-white font-semibold hover:bg-brand-dark text-sm"
          >
            + New feature
          </Link>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500">
          Filter category:
        </span>
        <button
          type="button"
          data-testid="features-filter-all"
          onClick={() => setFilterCat("")}
          className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
            filterCat === "" ? "bg-[#0A0A0A] border-[#0A0A0A] text-white" : "bg-white border-neutral-200 text-neutral-700"
          }`}
        >
          All
        </button>
        {site.categories.map((c) => (
          <button
            key={c.id}
            type="button"
            data-testid={`features-filter-${c.slug}`}
            onClick={() => setFilterCat((cur) => (cur === c.id ? "" : c.id))}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
              filterCat === c.id ? "bg-[#0A0A0A] border-[#0A0A0A] text-white" : "bg-white border-neutral-200 text-neutral-700 hover:border-neutral-900"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading && <p className="text-sm text-neutral-500">Loading…</p>}
        {!loading && items.length === 0 && (
          <p className="text-sm text-neutral-500">No features yet - create one.</p>
        )}
        {items.map((f) => {
          const cat = site.categories.find((c) => c.id === f.category_id);
          return (
            <Link
              key={f.id}
              to={`/admin/features/${f.id}`}
              data-testid={`feature-tile-${f.slug}`}
              className="group bg-white border border-neutral-200 rounded-xl overflow-hidden hover:shadow-lg transition-all"
            >
              <div
                className="aspect-[16/10] bg-neutral-100 bg-cover bg-center"
                style={pickImage(f) ? { backgroundImage: `url(${pickImage(f)})` } : {}}
              />
              <div className="p-4">
                <div className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                  {cat?.name || "-"} · {f.boxes?.length || 0} boxes
                </div>
                <div className="mt-1 font-display font-bold text-neutral-950 group-hover:text-brand-dark">
                  {f.title}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
